import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import ms from 'ms';
import { memo } from 'react';
import isEqual from 'react-fast-compare';

import { useIsMobileLayout } from '@/hooks';
import { SavedSearch } from '@/redux/api/saveSearch';
import { useAppDispatch } from '@/redux/hooks';
import { openConfirmUnfollowSearchModal } from '@/redux/modules/modal';
import { useTrackSearchUnfollowedAnalytics } from '@/utils/amplitude/analytics';
import { shortFormatDate } from '@/utils/dates';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { toFacetLabelPairs, toSavedSearchUrl } from '@/utils/savedSearchFacets';
import { addParamsToSearchUrl } from '@/utils/urls';

export interface ManageSavedSearchCardProps {
    search: SavedSearch;
}

const ManageSavedSearchCardInner = ({ search }: ManageSavedSearchCardProps) => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const formatMessage = useAppFormatMessage();
    const trackSearchUnfollowed = useTrackSearchUnfollowedAnalytics();

    const isMobileView = useIsMobileLayout();
    const searchLabels = toFacetLabelPairs(search.facets, intl);
    const searchInfoString = searchLabels.slice(1).join(', ');
    const createdDate = search.createdDate
        ? shortFormatDate(Date.parse(search.createdDate) / 1000)
        : formatMessage(['never']);
    const lastSentStatus = intl.formatNumber(search.resultCount);
    const savedSearchUrl = search?.searchURL || toSavedSearchUrl(search.facets);
    const publishDateMin = Math.floor((Date.now() - ms('24h')) / 1000);
    const followedSearchParams: Record<string, string | number | boolean> = {
        followed_search: true,
    };
    if ((search.resultCount ?? 0) > 0) {
        followedSearchParams.publishDateMin = publishDateMin;
    }
    const followedSearchHref = addParamsToSearchUrl(savedSearchUrl, followedSearchParams);

    const unfollowSearch = () => {
        dispatch(openConfirmUnfollowSearchModal(search.savedSearchId));
        trackSearchUnfollowed();
    };

    return isMobileView ? (
        <div className="flex flex-row items-center justify-between border-b border-border-secondary py-sm">
            <div className="flex flex-col items-start justify-center">
                <BodyPrimary>
                    <Link
                        className="text-left no-underline"
                        href={followedSearchHref}
                        target="_blank"
                    >
                        {searchLabels[0]}
                    </Link>
                </BodyPrimary>
                <BodyPrimary className="mt-2xs text-text-secondary">
                    {formatMessage(['search_alerts', 'mobileItemCount'], {
                        values: { count: lastSentStatus },
                    })}
                </BodyPrimary>
                <BodyPrimary className="mt-2xs text-text-secondary">
                    {searchLabels.length === 2
                        ? formatMessage(['search_alerts', 'singleFilter'])
                        : formatMessage(['search_alerts', 'filterCount'], {
                              values: { count: searchLabels.length - 1 },
                          })}
                </BodyPrimary>
            </div>

            <div className="flex items-center justify-end">
                <InlineButton onClick={unfollowSearch}>{formatMessage(['search_alerts', 'unfollow'])}</InlineButton>
            </div>
        </div>
    ) : (
        <div className="grid min-h-[68px] grid-cols-[auto_130px_140px_115px] border-b border-border-secondary py-xs">
            <div className="col-span-1 flex flex-col items-start justify-center py-xs pr-sm">
                <BodyPrimary>
                    <Link
                        className="text-left no-underline"
                        data-test-id="followedSearchItem"
                        href={followedSearchHref}
                        target="_blank"
                    >
                        {searchLabels[0]}
                    </Link>
                </BodyPrimary>
                <Caption>{searchInfoString}</Caption>
            </div>
            <div className="col-span-1 col-start-2 flex items-center justify-end text-right">
                <BodyPrimary className="text-text-secondary">{lastSentStatus}</BodyPrimary>
            </div>
            <div className="col-span-1 col-start-3 flex items-center justify-end">
                <BodyPrimary className="text-text-secondary">{createdDate}</BodyPrimary>
            </div>
            <div className="col-span-1 col-start-4 flex items-center justify-end">
                <InlineButton onClick={unfollowSearch}>{formatMessage(['search_alerts', 'unfollow'])}</InlineButton>
            </div>
        </div>
    );
};

export const ManageSavedSearchCard = memo<ManageSavedSearchCardProps>(ManageSavedSearchCardInner, isEqual);
